import React, { useState } from 'react';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { useAuth } from '../AuthContext';
import './ImageUploader.css';

/**
 * Componente per l'upload di immagini su Firebase Storage.
 * @param {string} currentImageUrl - L'URL dell'immagine attualmente salvata (usato in single mode).
 * @param {function} onUploadComplete - Funzione chiamata con il nuovo URL dopo l'upload.
 * @param {string} folderPath - La cartella in Storage.
 * @param {boolean} multiple - Se true, permette la selezione di più file.
 */
const ImageUploader = ({ currentImageUrl, onUploadComplete, folderPath = 'images', multiple = false }) => {
    const { currentUser } = useAuth();
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0); // Per upload singolo
    const [uploadStatus, setUploadStatus] = useState('');    // Per upload multiplo (es. "1 / 5")
    const [error, setError] = useState('');
    const [preview, setPreview] = useState(currentImageUrl);

    // Genera un ID univoco per l'input file per evitare conflitti se ci sono più uploader nella pagina
    const inputId = `file-input-${Math.random().toString(36).substr(2, 9)}`;

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // Se non è modalita multipla, prendi solo il primo file
        const filesToProcess = multiple ? files : [files[0]];

        // Validazione base per tutti i file
        for (let file of filesToProcess) {
            if (!file.type.startsWith('image/')) {
                setError('Seleziona solo file immagine validi.');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                setError('Alcune immagini superano il limite di 5MB.');
                return;
            }
        }

        setIsUploading(true);
        setError('');

        if (!multiple) {
            // --- LOGICA UPLOAD SINGOLO ---
            const file = filesToProcess[0];
            const reader = new FileReader();
            reader.onloadend = () => setPreview(reader.result);
            reader.readAsDataURL(file);

            const storage = getStorage();
            const fileName = `${folderPath}/${new Date().getTime()}-${file.name}`;
            const storageRef = ref(storage, fileName);
            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on('state_changed',
                (snapshot) => setUploadProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
                (err) => { setError('Errore upload.'); setIsUploading(false); setPreview(currentImageUrl); },
                async () => {
                    const url = await getDownloadURL(uploadTask.snapshot.ref);
                    onUploadComplete(url);
                    setIsUploading(false);
                }
            );
        } else {
            // --- LOGICA UPLOAD MULTIPLO ---
            let completedCount = 0;
            setUploadStatus(`0 / ${filesToProcess.length}`);
            
            // Crea un array di "Promesse" di upload
            const uploadPromises = filesToProcess.map(file => {
                return new Promise((resolve, reject) => {
                    const storage = getStorage();
                    const fileName = `${folderPath}/${new Date().getTime()}-${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
                    const storageRef = ref(storage, fileName);
                    const uploadTask = uploadBytesResumable(storageRef, file);

                    uploadTask.on('state_changed', null, 
                        (err) => { console.error(err); reject(err); },
                        async () => {
                            const url = await getDownloadURL(uploadTask.snapshot.ref);
                            completedCount++;
                            setUploadStatus(`${completedCount} / ${filesToProcess.length}`);
                            // Chiama onUploadComplete per OGNI file finito. 
                            // Il componente padre (es. galleria) aggiungerà questo URL al suo array.
                            onUploadComplete(url); 
                            resolve(url);
                        }
                    );
                });
            });

            try {
                // Aspetta che tutti i file finiscano di caricare
                await Promise.all(uploadPromises);
            } catch (err) {
                setError("Errore durante il caricamento di alcune immagini.");
            } finally {
                setIsUploading(false);
                setUploadStatus('');
                e.target.value = null; // Resetta l'input per permettere di riselezionare gli stessi file
            }
        }
    };

    const handleRemoveImage = () => {
        if (!currentImageUrl || multiple) return; // Disabilitato in modalità multipla

        if (window.confirm("Sei sicuro di voler rimuovere questa immagine?")) {
            onUploadComplete('');
            setPreview('');
            if (currentImageUrl.includes('firebasestorage.googleapis.com')) {
                const storage = getStorage();
                try {
                    deleteObject(ref(storage, currentImageUrl)).catch(err => console.error("Errore rimozione Storage:", err));
                } catch (error) {
                    console.error("URL non valido:", error);
                }
            }
        }
    };

    return (
        <div className={`image-uploader ${multiple ? 'multiple-mode' : 'single-mode'}`}>
            {/* Nascondi l'anteprima singola se siamo in modalità multipla */}
            {!multiple && (
                <div className="image-preview-container">
                    {preview ? <img src={preview} alt="Anteprima" className="image-preview" /> : <div className="no-image-placeholder">Nessuna Immagine</div>}
                </div>
            )}

            {isUploading && !multiple && (
                <div className="progress-bar-container">
                    <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                    <span>{Math.round(uploadProgress)}%</span>
                </div>
            )}
            
            {/* Testo di stato per upload multiplo */}
            {isUploading && multiple && (
                <div className="upload-status-text">
                    Caricamento in corso: <strong>{uploadStatus}</strong> completati
                </div>
            )}

            <div className="uploader-actions">
                <label htmlFor={inputId} className="upload-button">
                    {multiple ? 'Seleziona Più Immagini' : (currentImageUrl ? 'Sostituisci' : 'Carica Immagine')}
                </label>
                <input
                    id={inputId}
                    type="file"
                    onChange={handleFileChange}
                    accept="image/*"
                    multiple={multiple} /* ATTIVA SELEZIONE MULTIPLA SE PROP È TRUE */
                    disabled={isUploading}
                    style={{ display: 'none' }}
                />
                {!multiple && currentImageUrl && (
                    <button type="button" onClick={handleRemoveImage} className="remove-button" disabled={isUploading}>
                        Rimuovi
                    </button>
                )}
            </div>
             {error && <p className="uploader-error">{error}</p>}
        </div>
    );
};

export default ImageUploader;